import { useEffect, useState } from 'preact/hooks';
import { getCoachReport, coachReportCsvHref, type CoachReportRowApi } from '../lib/api';

// CoachReport — reviews completed, grouped by student, at `#/pro/report`
// (rung 5).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26` §9
//
// ⚑ IT IS A COUNT, NOT AN INVOICE, AND THIS WILL BE PUSHED ON. Somebody will
// ask for a rate box and a total in dollars. No: billing a coach's students is
// tenant→client money — Stripe Connect, System 2, explicitly deferred — and
// `73cb38c8` rules that Vowric is never the customer's book of record and never
// helps with taxes. A coach is a customer too. **We count; they invoice.**
// There is deliberately no currency anywhere in this file.
//
// ⚑ IT READS `coach_review_log` ALONE, like the rest of the history. A report
// assembled from live grants would look identical the day it shipped and would
// quietly under-count further and further back as links lapsed — and a report
// about work done that silently loses rows is worse than no report.

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: CoachReportRowApi[]; total: number }
  | { kind: 'error'; message: string };

function monthsAgoIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString();
}

const RANGES: { key: string; label: string; from?: string }[] = [
  { key: 'all', label: 'All time' },
  { key: '3m', label: 'Last 3 months' },
  { key: '12m', label: 'Last 12 months' },
];

function readable(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const REPORT_FRAME = 'We couldn’t build your report.';

export function CoachReport() {
  const [rangeKey, setRangeKey] = useState('all');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  const from = rangeKey === '3m' ? monthsAgoIso(3) : rangeKey === '12m' ? monthsAgoIso(12) : undefined;

  useEffect(() => {
    let cancelled = false;
    setPhase({ kind: 'loading' });
    getCoachReport({ from })
      .then((r) => {
        if (cancelled) return;
        setPhase({ kind: 'ready', rows: r.rows, total: r.totalReviews });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // An empty report and a broken one must not read the same — the
        // recurring failure mode of this whole surface.
        setPhase({
          kind: 'error',
          message: err instanceof Error ? err.message : REPORT_FRAME,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [from]);

  return (
    <div class="coach-report">
      <h1 class="coach__title">Reviews completed</h1>

      <div class="coach-report__controls">
        <select
          class="coach-report__range"
          aria-label="Date range"
          value={rangeKey}
          onChange={(e) => setRangeKey((e.target as HTMLSelectElement).value)}
        >
          {RANGES.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </select>
        {/* A plain link, not a fetch-and-blob: the browser keeps the filename
            the server chose in Content-Disposition, which a blob download
            would discard. */}
        <a class="coach-report__csv" href={coachReportCsvHref({ from })} download>
          Download CSV
        </a>
      </div>

      {phase.kind === 'loading' && <p class="coach__quiet">Counting…</p>}

      {phase.kind === 'error' && (
        // The frame once, the server's sentence only when it adds one.
        <p class="coach__quiet">
          {phase.message === REPORT_FRAME ? REPORT_FRAME : `${REPORT_FRAME} ${phase.message}`}
        </p>
      )}

      {phase.kind === 'ready' && phase.rows.length === 0 && (
        <p class="coach__quiet">No reviews in this period.</p>
      )}

      {phase.kind === 'ready' && phase.rows.length > 0 && (
        <>
          <ul class="coach-report__list">
            {phase.rows.map((r) => (
              <li key={r.student ?? '—'} class="coach-report__row">
                <span class="coach-report__student">
                  {/* A note whose household never named the performer still
                      COUNTS — it just has no name. Dropping it would make the
                      total disagree with the log. */}
                  {r.student ?? 'Unnamed'}
                </span>
                <span class="coach-report__span">
                  {readable(r.firstReviewedAt)} – {readable(r.lastReviewedAt)}
                </span>
                <span class="coach-report__count">{r.reviews}</span>
              </li>
            ))}
          </ul>
          <p class="coach-report__total">
            {phase.total} {phase.total === 1 ? 'review' : 'reviews'} in total
          </p>
        </>
      )}
    </div>
  );
}
